import { Button, Modal, Stack, Text } from "@assetwatch/ui";
import { useEffect, useState } from "react";
import {
  type BulkActionOutcome,
  useBulkAssignToMeMutation,
} from "../../queries/useWorkOrderBulkActionsMutations";
import type { WorkOrder } from "../../queries/useWorkOrdersQuery";
import { displayWorkOrderNumber } from "../snowflakes/work-orders/presentation";
import {
  assignmentIneligibilityReason,
  isEligibleForAssignment,
} from "./bulkEligibility";

type Step = "review" | "results";

export function BulkAssignToMeModal({
  open,
  onOpenChange,
  workOrders,
  currentUserId,
  onOutcome,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrders: WorkOrder[];
  currentUserId: number | null;
  onOutcome: (outcome: BulkActionOutcome) => void;
}) {
  const mutation = useBulkAssignToMeMutation();
  const [step, setStep] = useState<Step>("review");
  const [outcome, setOutcome] = useState<BulkActionOutcome | null>(null);

  const eligible = workOrders.filter((workOrder) =>
    isEligibleForAssignment(workOrder, currentUserId),
  );
  const ineligible = workOrders.filter(
    (workOrder) => !isEligibleForAssignment(workOrder, currentUserId),
  );

  useEffect(() => {
    if (open) {
      setStep("review");
      setOutcome(null);
      mutation.reset();
    }
  }, [open, mutation.reset]);

  async function confirm() {
    if (currentUserId === null) return;
    try {
      const result = await mutation.mutateAsync({
        targets: eligible.map((workOrder) => ({
          id: workOrder.id,
          number: workOrder.number,
        })),
        userId: currentUserId,
      });
      setOutcome(result);
      onOutcome(result);
      setStep("results");
    } catch {
      // mutation.isError drives the inline error message; stay on the review step.
    }
  }

  return (
    <Modal
      onOpenChange={onOpenChange}
      open={open}
      size="md"
      title={step === "review" ? "Assign Work Orders to me" : "Assign results"}
    >
      {step === "review" ? (
        <Stack gap={16}>
          <Text>
            {eligible.length} of {workOrders.length} selected Work Orders will
            be assigned to you.
          </Text>
          {ineligible.length > 0 ? (
            <Stack gap={8}>
              <Text size="sm" tone="subtle">
                Not eligible
              </Text>
              {ineligible.map((workOrder) => (
                <div
                  className="flex items-center justify-between gap-(--spacing-8) rounded-(--radius-sm) border border-outline-subtle px-(--spacing-12) py-(--spacing-8)"
                  key={workOrder.id}
                >
                  <Text size="sm">
                    {displayWorkOrderNumber(workOrder.number)} —{" "}
                    {workOrder.title}
                  </Text>
                  <Text size="sm" tone="subtle">
                    {assignmentIneligibilityReason(workOrder, currentUserId)}
                  </Text>
                </div>
              ))}
            </Stack>
          ) : null}
          {mutation.isError ? (
            <Text tone="negative">
              Unable to assign the selected Work Orders. Try again.
            </Text>
          ) : null}
          <div className="flex justify-end gap-(--spacing-8)">
            <Button onClick={() => onOpenChange(false)} variant="outline">
              Cancel
            </Button>
            <Button
              disabled={
                eligible.length === 0 ||
                currentUserId === null ||
                mutation.isPending
              }
              loading={mutation.isPending}
              loadingText="Assigning"
              onClick={() => void confirm()}
            >
              Assign to me
            </Button>
          </div>
        </Stack>
      ) : (
        <Stack gap={16}>
          <Text>
            {outcome?.succeeded.length ?? 0} of{" "}
            {(outcome?.succeeded.length ?? 0) + (outcome?.failed.length ?? 0)}{" "}
            Work Orders were assigned to you.
          </Text>
          {outcome && outcome.failed.length > 0 ? (
            <Stack gap={8}>
              <Text size="sm" tone="negative">
                Could not be assigned
              </Text>
              {outcome.failed.map((item) => (
                <div
                  className="flex items-center justify-between gap-(--spacing-8) rounded-(--radius-sm) border border-outline-subtle px-(--spacing-12) py-(--spacing-8)"
                  key={item.id}
                >
                  <Text size="sm">{displayWorkOrderNumber(item.number)}</Text>
                  <Text size="sm" tone="negative">
                    {item.reason}
                  </Text>
                </div>
              ))}
            </Stack>
          ) : null}
          <div className="flex justify-end gap-(--spacing-8)">
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </Stack>
      )}
    </Modal>
  );
}
